/**
 * PATH: packages/ui/src/components/email/CalendarInviteCard.tsx
 * FILE_ID: UI-EMAIL-CALENDARINVITECARD-001
 * PURPOSE: Shared, package-local Calendar-invite banner for the
 *          @quotech/ui baseline. Renders the meeting-invite /
 *          cancellation / response summary carried by an email's
 *          `EmailCalendarEvent` sidecar.
 * WHY:     The email-hub already ships a near-identical banner
 *          (packages/email-hub/.../CalendarInviteCard.tsx), but the
 *          Kanban card-detail Email tab (`EmailThreadView`) lives in
 *          @quotech/ui and CANNOT import from @quotech/email-hub —
 *          that edge is a dependency cycle (email-hub depends on ui).
 *          Operator (2026-06-07): "Was this a cancellation of meeting?
 *          If so I should be able to see the calendar here." This is
 *          the ui-side twin so the invite/cancellation summary shows
 *          inside the board card drawer, not just the standalone Email
 *          Hub reader.
 * RELATED:
 *   - packages/email-hub/src/feature/components/CalendarInviteCard.tsx:
 *     the standalone-reader twin (keep visually in sync).
 *   - packages/ui/src/components/email/types.ts: EmailCalendarEventView.
 *   - packages/ui/src/kanban/components/EmailThreadView.tsx: consumer.
 */

import {
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  HelpCircle,
  MapPin,
  Users,
  XCircle,
} from 'lucide-react'

import type { EmailCalendarEventView } from './types'

interface Props {
  event: EmailCalendarEventView
  className?: string
}

/**
 * Calendar-invite banner shown above the email body. Degrades to a
 * "limited details" line when only stub data is available (e.g. the
 * backend couldn't `$expand=event` because the OAuth grant lacks
 * `Calendars.Read`).
 */
export function CalendarInviteCard({ event, className }: Props) {
  const isCancelled = event.meeting_message_type === 'meetingCancelled'
  const isResponse = [
    'meetingAccepted',
    'meetingTentativelyAccepted',
    'meetingDeclined',
  ].includes(event.meeting_message_type)
  const responseTone =
    event.meeting_message_type === 'meetingAccepted'
      ? 'accepted'
      : event.meeting_message_type === 'meetingDeclined'
        ? 'declined'
        : event.meeting_message_type === 'meetingTentativelyAccepted'
          ? 'tentative'
          : null

  const hasStartEnd = !!event.start_at
  const isStub = !hasStartEnd && !event.organizer_email && !event.location

  return (
    <section
      className={[
        'rounded-lg border overflow-hidden',
        isCancelled
          ? 'border-red-300 bg-red-50/70 dark:border-red-900/50 dark:bg-red-950/30'
          : 'border-blue-300 bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-950/20',
        className ?? '',
      ].join(' ')}
      aria-label="Calendar invite"
    >
      <header
        className={[
          'flex items-center gap-2 px-4 py-2 text-xs font-medium uppercase tracking-wide',
          isCancelled
            ? 'text-red-700 dark:text-red-300'
            : 'text-blue-700 dark:text-blue-300',
        ].join(' ')}
      >
        <Calendar className="h-3.5 w-3.5" />
        <span>
          {isCancelled
            ? 'Meeting cancelled'
            : isResponse
              ? `Meeting ${responseTone ?? 'response'}`
              : event.meeting_message_type === 'meetingForwardNotification'
                ? 'Meeting forwarded'
                : 'Calendar invite'}
        </span>
        {responseTone === 'accepted' && (
          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
        )}
        {responseTone === 'declined' && (
          <XCircle className="h-3.5 w-3.5 text-red-600" />
        )}
        {responseTone === 'tentative' && (
          <HelpCircle className="h-3.5 w-3.5 text-amber-600" />
        )}
      </header>

      <div className="space-y-2 px-4 pb-4 pt-1">
        {event.subject && (
          <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {event.subject}
          </div>
        )}

        {hasStartEnd ? (
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-zinc-300">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>{formatRange(event)}</span>
          </div>
        ) : isStub ? (
          <div className="text-xs italic text-neutral-500 dark:text-zinc-400">
            Limited details — open the original message in Outlook to accept,
            decline, or see the full invite.
          </div>
        ) : null}

        {event.location && (
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-zinc-300">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        )}

        {event.organizer_email && (
          <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-zinc-300">
            <Users className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              Organised by{' '}
              <span className="text-neutral-900 dark:text-neutral-100">
                {event.organizer_name || event.organizer_email}
              </span>
              {event.attendees && event.attendees.length > 0 && (
                <>
                  {' '}
                  · {event.attendees.length}{' '}
                  {event.attendees.length === 1 ? 'attendee' : 'attendees'}
                </>
              )}
            </span>
          </div>
        )}

        {event.is_online_meeting && event.online_meeting_join_url && (
          <a
            href={event.online_meeting_join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
          >
            Join {prettyMeetingProvider(event.online_meeting_provider) || 'meeting'}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </section>
  )
}

function prettyMeetingProvider(provider?: string): string {
  switch (provider) {
    case 'teamsForBusiness':
      return 'Teams meeting'
    case 'skypeForBusiness':
    case 'skypeForConsumer':
      return 'Skype meeting'
    case undefined:
    case '':
      return ''
    default:
      return provider
  }
}

function formatRange(event: EmailCalendarEventView): string {
  if (!event.start_at) return ''
  const start = new Date(event.start_at)
  if (Number.isNaN(start.getTime())) return ''
  const end = event.end_at ? new Date(event.end_at) : null

  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year:
      start.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  }

  const startDate = start.toLocaleDateString(undefined, dateOpts)
  const startTime = event.is_all_day
    ? 'all day'
    : start.toLocaleTimeString(undefined, timeOpts)

  if (event.is_all_day) {
    return `${startDate} · all day`
  }

  if (!end || Number.isNaN(end.getTime())) {
    return `${startDate} · ${startTime}`
  }

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()

  const endTime = end.toLocaleTimeString(undefined, timeOpts)
  if (sameDay) {
    return `${startDate} · ${startTime} – ${endTime}`
  }
  const endDate = end.toLocaleDateString(undefined, dateOpts)
  return `${startDate} ${startTime} → ${endDate} ${endTime}`
}

export default CalendarInviteCard
